import {
  Alert,
  Box,
  Checkbox,
  Divider,
  FormControlLabel,
  Paper,
  Typography,
} from "@mui/material";
import React from "react";
import { IContactNotifications } from "../../Interfaces/ICompanyContact";
import { ICompanyOffice } from "../../Interfaces";
import {
  notificationSummary,
  RESULT_TYPE_LABELS,
} from "../../Tools/ContactUtils";

type Props = {
  notifications: IContactNotifications;
  onChange: (changes: Partial<IContactNotifications>) => void;
  occasions: { key: string; label: string }[];
  sites: ICompanyOffice[];
  disabled?: boolean;
};

/**
 * Result notifications for one contact (S4.3, story 5606).
 *
 * The panel only renders for a contact who holds Results Access — the caller decides that, and
 * removing the grant hides the panel without discarding what is stored. All four result types are on
 * for a new contact. The scope toggle is off by default, meaning every occasion and site the contact
 * can access; ticking it reveals the occasion and site lists. A contact may validly hold Results
 * Access with nothing ticked, which the summary states plainly.
 */
const ContactNotificationPanel: React.FC<Props> = ({
  notifications,
  onChange,
  occasions,
  sites,
  disabled,
}) => {
  const toggleOccasion = (key: string, checked: boolean) =>
    onChange({
      occasions: checked
        ? [...(notifications.occasions || []), key]
        : (notifications.occasions || []).filter((x) => x !== key),
    });

  const toggleSite = (siteID: number, checked: boolean) =>
    onChange({
      siteIDs: checked
        ? [...(notifications.siteIDs || []), siteID]
        : (notifications.siteIDs || []).filter((x) => x !== siteID),
    });

  return (
    <Paper elevation={0} sx={{ p: 2, mt: 2, border: "1px solid #e0e0e0" }}>
      <Typography variant="subtitle2" fontWeight={600}>
        Result notifications
      </Typography>

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
        {RESULT_TYPE_LABELS.map((type) => (
          <FormControlLabel
            key={type.key}
            control={
              <Checkbox
                size="small"
                disabled={disabled}
                checked={Boolean(notifications[type.key])}
                onChange={(e) => onChange({ [type.key]: e.target.checked } as any)}
              />
            }
            label={type.label}
          />
        ))}
      </Box>

      <Divider sx={{ my: 1 }} />

      <FormControlLabel
        control={
          <Checkbox
            size="small"
            disabled={disabled}
            checked={notifications.limitScope}
            onChange={(e) => onChange({ limitScope: e.target.checked })}
          />
        }
        label="Limit to specific sites/occasions"
      />

      {notifications.limitScope && (
        <Box sx={{ pl: 3 }}>
          <Typography variant="caption" color="text.secondary" display="block">
            Occasions
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {occasions.map((occasion) => (
              <FormControlLabel
                key={occasion.key}
                control={
                  <Checkbox
                    size="small"
                    disabled={disabled}
                    checked={(notifications.occasions || []).includes(occasion.key)}
                    onChange={(e) => toggleOccasion(occasion.key, e.target.checked)}
                  />
                }
                label={occasion.label}
              />
            ))}
          </Box>

          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
            Sites
          </Typography>
          {sites.length === 0 ? (
            <Typography variant="caption">
              No sites yet — add them on step 3 to narrow notifications by site.
            </Typography>
          ) : (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              {sites.map((site) => (
                <FormControlLabel
                  key={site.companySiteID}
                  control={
                    <Checkbox
                      size="small"
                      disabled={disabled}
                      checked={(notifications.siteIDs || []).includes(site.companySiteID)}
                      onChange={(e) => toggleSite(site.companySiteID, e.target.checked)}
                    />
                  }
                  label={site.officeName}
                />
              ))}
            </Box>
          )}
        </Box>
      )}

      {/* The plain-English restatement, which changes as the ticks change (S4.3). */}
      <Alert severity="info" sx={{ mt: 2 }}>
        {notificationSummary(notifications, occasions, sites)}
      </Alert>
    </Paper>
  );
};

export default ContactNotificationPanel;
